import React from "react";
import { cn } from "../../utils/cn";
import { BUTTON_VARIANT } from "../../constants/commonConstants";
import { ClipLoader } from "react-spinners";

const Button = ({
  children,
  buttonText = "Button",
  variant = BUTTON_VARIANT.PRIMARY,
  size = "default",
  loading = false,
  pulse = false,
  disabled = false,
  className = "",
  wrapperClassName = "",
  icon,
  iconPosition = "left",
  loaderColor = "var(--color-content)",
  hoverEffect = "lighten", // scale, glow, slide, darken, lighten, border, none
  showTooltip = false,
  tooltipMessage = "",
  onClick,
  ...props
}) => {

  const getHoverClasses = () => {
    if (disabled) return "";

    switch (hoverEffect) {
      case "scale":
        return "hover:scale-105 active:scale-95";
      case "glow":
        return "hover:shadow-[0_0_15px_rgba(0,123,255,0.5)]";
      case "slide":
        return "relative overflow-hidden before:absolute before:content-[''] before:top-0 before:left-0 before:w-full before:h-full before:bg-white before:opacity-0 before:transition-opacity hover:before:opacity-20";
      case "darken":
        return "hover:brightness-90";
      case "lighten":
        return "hover:brightness-110";
      case "border":
        return "hover:border-2";
      case "none":
      default:
        return "";
    }
  };

  if (pulse) {
    return (
      <div
        className={cn(
          "flex items-center justify-center border gap-2 rounded-xl font-medium focus:outline-none animate-pulse text-transparent",
          className
        )}
      >
        Loading
      </div>
    );
  }

  const baseClasses = cn(
    "btn-base flex items-center justify-center gap-2 rounded-xl font-medium focus:outline-none transition-all duration-300 ease-in-out",
    { "btn-loading": loading },

    // Size classes
    {
      "btn-sm": size === "sm" || size === "small",
      "btn-md": size === "default" || size === "md",
      "btn-lg": size === "lg" || size === "large",
    },

    // Variant classes
    {
      "btn-primary": variant === BUTTON_VARIANT.PRIMARY,
      "btn-secondary": variant === BUTTON_VARIANT.SECONDARY,
      "btn-outline": variant === BUTTON_VARIANT.OUTLINE,
      "btn-ghost": variant === BUTTON_VARIANT.GHOST,
      "btn-plain": variant === BUTTON_VARIANT.PLAIN,
      "btn-icon": variant === BUTTON_VARIANT.ICON,
      "btn-link": variant === BUTTON_VARIANT.LINK,
      "btn-success": variant === BUTTON_VARIANT.SUCCESS,
      "btn-error": variant === BUTTON_VARIANT.ERROR || variant === BUTTON_VARIANT.DANGER,
      "btn-theme": variant === BUTTON_VARIANT.THEME,
      "btn-gradient-border": variant === BUTTON_VARIANT.BORDER_GRADIENT,
      "btn-primary-next": variant === BUTTON_VARIANT.PRIMARY_NEXT,
      "btn-login": variant === BUTTON_VARIANT.LOGIN,
      "btn-login2": variant === BUTTON_VARIANT.LOGIN2,
      "btn-edit": variant === BUTTON_VARIANT.EDIT,
    },

    // Hover effect classes
    {
      "hover:scale-105 active:scale-95": hoverEffect === "scale",
      "hover:shadow-[0_0_15px_rgba(0,123,255,0.5)]": hoverEffect === "glow",
      "hover:brightness-110": hoverEffect === "lighten",
      "hover:brightness-90": hoverEffect === "darken",
    },

    className
  );

  return (
    <div className={cn("w-full h-full relative group", wrapperClassName)}>
      <button
        onClick={onClick}
        disabled={disabled || loading}
        className={cn(baseClasses, disabled && "pointer-events-none")}
        {...props}
      >
        {loading ? null : (
          <>
            {icon && iconPosition === "left" && <span className="mr-2">{icon}</span>}
            {variant !== BUTTON_VARIANT.ICON && <span>{children || buttonText}</span>}
            {icon && iconPosition === "right" && <span className="ml-2">{icon}</span>}
            {icon && iconPosition === "center" && (<span className="flex items-center justify-center w-full"> {icon}</span>
            )}
          </>
        )}
      </button>

      {showTooltip && tooltipMessage && (
        <div className="absolute px-2 py-1 mb-2 text-xs text-white transition-opacity transform -translate-x-1/2 bg-black rounded opacity-0 bottom-full left-1/2 group-hover:opacity-100">
          {tooltipMessage}
        </div>
      )}
    </div>
  );
};

export default Button;
